import type {
	PricingEstimationCostBreakdown,
	PricingEstimationService,
	PricingEstimationState,
	PricingEstimationTotal,
	PricingEstimationType,
	PricingEstimationTypeFixed,
	PricingEstimationTypeRange,
} from '../types';

import type {
	AiCostBreakdown,
	CostBreakdownCategory,
	PricingEstimationTypeUnit,
} from '@typings/vendor/properties';
import {
	calculateTotalPricingEstimation,
	prioritizePricingEstimationValues,
	transformCostBreakdownElementsToPricingEstimations,
} from '../utils/pricing-estimation';

type PricingParams = {
	guestCount: number;
	year?: number | null;
	month?: number | null;
	day?: number | null;
	selectedCategories?: PricingEstimationService[] | null;
	pricingEstimations?:
		| (PricingEstimationState | PricingEstimationCostBreakdown)[]
		| null;
	// Pre-parsed numeric starting cost from startingCostAtom. When provided,
	// the engine clamps totalCost.minCost to max(computedMin, startingCost)
	// after the per-category summation (see pricing-estimation.ts).
	startingCost?: number | null;
};

export const usePricingEstimation = (
	params: PricingParams,
): PricingEstimationTotal | null => {
	const {
		guestCount,
		month,
		selectedCategories,
		pricingEstimations,
		startingCost,
	} = params;

	const pricingEstimationsTransformed =
		transformCostBreakdownElementsToPricingEstimations(
			pricingEstimations,
			month,
		);

	const pricingValuesPrioritized = prioritizePricingEstimationValues(
		pricingEstimationsTransformed || null,
	);

	return calculateTotalPricingEstimation(
		pricingValuesPrioritized,
		guestCount,
		selectedCategories,
		month,
		startingCost ?? null,
	);
};

/** @internal */
export const displayCostValue = (
	pricingEstimationType:
		| PricingEstimationTypeRange
		| PricingEstimationTypeFixed,
): string => {
	const unitType = pricingEstimationType.unit || null;
	if (pricingEstimationType.cost != null)
		return displayCostString(pricingEstimationType.cost, null, unitType);

	return displayCostString(
		pricingEstimationType.minCost,
		pricingEstimationType.maxCost,
		unitType,
	);
};

const displayCostString = (
	minCost?: number | null,
	maxCost?: number | null,
	unitType?: PricingEstimationTypeUnit,
): string => {
	const formattedMinCost = minCost != null ? formatCurrency(minCost) : null;
	const formattedMaxCost = maxCost != null ? formatCurrency(maxCost) : null;
	const formattedCost =
		formattedMinCost &&
		formattedMaxCost &&
		formattedMinCost !== formattedMaxCost
			? `${formattedMinCost} - ${formattedMaxCost}`
			: formattedMinCost || formattedMaxCost;

	if (!formattedCost) return 'Contact for price';
	return unitType === 'PER_GUEST'
		? `${formattedCost} per guest`
		: formattedCost;
};

const usdCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
});

export const formatCurrency = (value: number): string => {
	return usdCurrencyFormatter.format(value);
};

export const transformAiCostBreakdownToPricingEstimation = (
	aiCostBreakdown: AiCostBreakdown | null,
): PricingEstimationState => {
	if (!aiCostBreakdown) {
		return {
			ceremony: null,
			reception: null,
			catering: null,
			barServices: null,
			peakMonths: null,
			offPeakMonths: null,
		};
	}

	return {
		ceremony: transformAiCostBreakdownCategoryToPricingEstimationType(
			aiCostBreakdown.ceremony ?? null,
		),
		reception: transformAiCostBreakdownCategoryToPricingEstimationType(
			aiCostBreakdown.reception ?? null,
		),
		catering: transformAiCostBreakdownCategoryToPricingEstimationType(
			aiCostBreakdown.catering ?? null,
		),
		barServices: transformAiCostBreakdownCategoryToPricingEstimationType(
			aiCostBreakdown.barServices ?? null,
		),
		peakMonths: null,
		offPeakMonths: null,
	};
};

const VALID_PRICING_UNITS: ReadonlySet<string> = new Set([
	'PER_GUEST',
	'TOTAL',
]);

const parseUnit = (
	unit: PricingEstimationTypeUnit,
): PricingEstimationTypeUnit => {
	if (unit != null && VALID_PRICING_UNITS.has(unit)) {
		return unit;
	}
	return null;
};

const transformAiCostBreakdownCategoryToPricingEstimationType = (
	costBreakdownCategory: CostBreakdownCategory | null,
): PricingEstimationType => {
	if (!costBreakdownCategory || !costBreakdownCategory.price) return null;

	const { price } = costBreakdownCategory;
	const unit = parseUnit(price.unit);

	if (price.amount.value != null) {
		return {
			cost: price.amount.value,
			unit,
			currency: 'USD',
			dataSource: 'AI',
		};
	}

	if (price.amount.min != null && price.amount.max != null) {
		return {
			minCost: price.amount.min,
			maxCost: price.amount.max,
			unit,
			currency: 'USD',
			dataSource: 'AI',
		};
	}

	// Handle partial range: treat single bound as a fixed value
	if (price.amount.min != null) {
		return {
			cost: price.amount.min,
			unit,
			currency: 'USD',
			dataSource: 'AI',
		};
	}

	if (price.amount.max != null) {
		return {
			cost: price.amount.max,
			unit,
			currency: 'USD',
			dataSource: 'AI',
		};
	}

	return null;
};
